import React, { useState, useMemo } from "react";
import { Download } from "lucide-react";
import { APP_IDS } from "../constants/testIds";
import { exportLogCsv } from "../utils/exportCsv";

const TYPE_COLOR = {
  sold: "var(--status-sold-bg)",
  reserved: "var(--brand-primary)",
  released: "var(--semantic-success)",
  funds_received: "var(--semantic-danger)",
  reassigned: "var(--brand-primary)",
};

export const LogTab = ({ log, year, currentUser }) => {
  const [q, setQ] = useState("");
  const isAdmin = currentUser?.role === "admin";

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return log;
    return log.filter((e) =>
      [e.text, e.seller, e.type, e.time].filter(Boolean).some((v) => v.toLowerCase().includes(term))
    );
  }, [log, q]);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <div className="bg-[color:var(--surface-card)] border rounded-lg p-5" style={{ borderColor: "var(--border-default)" }}>
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <h2 className="text-base font-semibold" style={{ color: "var(--ink-primary)" }}>Activity log</h2>
          {isAdmin && log.length > 0 && (
            <button
              data-testid="log-export-csv"
              onClick={() => exportLogCsv(log, year)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-card)] text-[color:var(--ink-secondary)] shadow-sm transition-colors hover:bg-[color:var(--surface-subtle)] hover:border-[color:var(--brand-primary)] hover:text-[color:var(--brand-primary)]"
            >
              <Download className="h-3.5 w-3.5" />
              Export CSV
            </button>
          )}
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, ticket #, seller…"
          data-testid={APP_IDS.logSearch}
          className="w-full rounded-md border bg-[color:var(--surface-card)] px-3 py-2 text-base transition-colors focus:outline-none focus:border-[color:var(--brand-primary)] focus:ring-2 focus:ring-[color:var(--brand-primary)]/25 mb-4"
          style={{ borderColor: "var(--border-default)" }}
        />

        {log.length === 0 ? (
          <p className="text-sm py-8 text-center" style={{ color: "var(--ink-tertiary)" }}>No activity recorded yet.</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm py-8 text-center" style={{ color: "var(--ink-tertiary)" }}>No matching records.</p>
        ) : (
          <ul className="divide-y" style={{ borderColor: "var(--border-default)" }}>
            {filtered.map((e, i) => {
              const stableKey = `${e.time}-${e.type}-${e.ticket_number ?? "x"}-${i}`;
              return (
                <li key={stableKey} data-testid={APP_IDS.logEntry(i)} className="py-3 flex items-start gap-3">
                  <span
                    className="mt-1 inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                    style={{ background: TYPE_COLOR[e.type] || "var(--ink-tertiary)" }}
                    aria-hidden
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm" style={{ color: "var(--ink-primary)" }}>{e.text}</p>
                    {e.seller && (
                      <p className="text-xs mt-0.5" style={{ color: "var(--ink-tertiary)" }}>by {e.seller}</p>
                    )}
                  </div>
                  <p className="text-xs whitespace-nowrap" style={{ color: "var(--ink-tertiary)" }}>{e.time}</p>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
};
